/**
 * Locale and time zone, as values a tenant or a person can choose.
 *
 * Kept apart from the formatter so the server can validate a stored choice
 * without loading the display code.
 *
 * @module utils/region
 */

export const DEFAULT_LOCALE = "en-IE";
export const DEFAULT_TIME_ZONE = "Europe/Dublin";

/** The widths of `users.locale` and `users.time_zone`. */
export const LOCALE_MAX_LENGTH = 35;
export const TIME_ZONE_MAX_LENGTH = 64;

/** Whether `locale` is a well-formed BCP 47 tag the runtime can format in. */
export function isValidLocale(locale: string): boolean {
  if (locale.length > LOCALE_MAX_LENGTH) {
    return false;
  }
  try {
    return Intl.DateTimeFormat.supportedLocalesOf(locale).length > 0;
  } catch {
    return false;
  }
}

/** Whether `timeZone` is an IANA zone the runtime knows. */
export function isValidTimeZone(timeZone: string): boolean {
  if (timeZone.length > TIME_ZONE_MAX_LENGTH) {
    return false;
  }
  try {
    new Intl.DateTimeFormat(DEFAULT_LOCALE, { timeZone }).format(0);
    return true;
  } catch {
    return false;
  }
}

/** Locales offered in a picker: the ones the product is used in. */
export const LOCALE_TAGS = [
  "en-IE",
  "en-GB",
  "en-US",
  "ga-IE",
  "fr-FR",
  "de-DE",
  "es-ES",
  "it-IT",
  "nl-NL",
  "pl-PL",
] as const;

export interface RegionOption {
  value: string;
  label: string;
}

/** A locale named in English with its tag: `English (Ireland) (en-IE)`. */
export function localeLabel(tag: string): string {
  const names = new Intl.DisplayNames(["en"], { type: "language", languageDisplay: "standard" });
  return `${names.of(tag) ?? tag} (${tag})`;
}

/**
 * Locale choices named in English, with `current` kept in the list when it is
 * not one of the offered tags so a stored value never vanishes from its own
 * picker.
 */
export function localeOptions(current?: string): RegionOption[] {
  const tags: string[] = [...LOCALE_TAGS];
  if (current && !tags.includes(current)) {
    tags.push(current);
  }
  return tags.map((tag) => ({ value: tag, label: localeLabel(tag) }));
}

/** Every IANA zone the runtime knows, with `current` kept in when it is not one. */
export function timeZoneOptions(current?: string): RegionOption[] {
  const zones = Intl.supportedValuesOf("timeZone");
  if (!zones.includes(DEFAULT_TIME_ZONE)) {
    zones.push(DEFAULT_TIME_ZONE);
  }
  if (current && !zones.includes(current)) {
    zones.push(current);
  }
  return zones.map((zone) => ({ value: zone, label: zone.replaceAll("_", " ") }));
}
